/**
 * Detects whether a media item represents an audio resource based on MIME type, file name, or URL.
 * @param {string} [contentType] - The MIME content type.
 * @param {string} [fileName] - The file name.
 * @param {string} [url] - The resource URL.
 * @returns {boolean} True if audio.
 */
export function isAudioMedia(contentType?: string, fileName?: string, url?: string): boolean {
  const normType = contentType?.toLowerCase()?.trim() || "";
  if (normType.startsWith("audio/")) return true;

  const audioExtRegex = /\.(mp3|wav|ogg|m4a|aac|flac)(\?.*)?$/i;
  if (fileName && audioExtRegex.test(fileName.trim())) return true;
  if (url && audioExtRegex.test(url.trim())) return true;

  return false;
}

/**
 * Detects whether a media item represents a video resource based on MIME type, file name, or URL.
 * @param {string} [contentType] - The MIME content type.
 * @param {string} [fileName] - The file name.
 * @param {string} [url] - The resource URL.
 * @returns {boolean} True if video.
 */
export function isVideoMedia(contentType?: string, fileName?: string, url?: string): boolean {
  const normType = contentType?.toLowerCase()?.trim() || "";
  if (normType.startsWith("video/")) return true;

  const videoExtRegex = /\.(mp4|mov|webm|m4v|mkv|avi|ogv)(\?.*)?$/i;
  if (fileName && videoExtRegex.test(fileName.trim())) return true;
  if (url && videoExtRegex.test(url.trim())) return true;

  return false;
}

/**
 * Resolves the appropriate MIME type for video playback.
 * Avoids falling back to generic non-playable MIME types like "application/octet-stream".
 *
 * @param {string} [contentType] - The MIME content type.
 * @param {string} [fileName] - The file name.
 * @param {string} [url] - The resource URL.
 * @returns {string} The resolved video MIME type (e.g. "video/mp4", "video/webm", "video/ogg").
 */
export function resolveVideoMimeType(contentType?: string, fileName?: string, url?: string): string {
  const normType = contentType?.toLowerCase()?.trim() || "";
  if (normType.startsWith("video/") && normType !== "video/*") {
    return normType;
  }

  const check = (s?: string) => s?.toLowerCase()?.trim() || "";
  const target = `${check(fileName)} ${check(url)}`;

  if (/\.webm(\?|$|\s)/.test(target)) return "video/webm";
  if (/\.(ogv|ogg)(\?|$|\s)/.test(target)) return "video/ogg";
  if (/\.(mov|quicktime)(\?|$|\s)/.test(target)) return "video/quicktime";
  if (/\.(m4v|mp4)(\?|$|\s)/.test(target)) return "video/mp4";

  return "video/mp4";
}

/**
 * Resolves the primary URL and optional fallback URL for a media item.
 * Sanitizes URLs (e.g. encodes raw spaces for Android Glide/OkHttp compliance) and validates formats.
 * @param {{ downloadUrl?: string; thumbnailUrl?: string }} media - The media item model.
 * @returns {{ primaryUrl: string; fallbackUrl?: string }} The resolved URLs.
 */
export function resolveMediaUrls(media?: { downloadUrl?: string; thumbnailUrl?: string }): {
  primaryUrl: string;
  fallbackUrl?: string;
} {
  const rawThumb = typeof media?.thumbnailUrl === "string" ? media.thumbnailUrl.trim() : "";
  const rawDownload = typeof media?.downloadUrl === "string" ? media.downloadUrl.trim() : "";

  const hasValidThumb =
    rawThumb.length > 0 &&
    (rawThumb.startsWith("http") ||
      rawThumb.startsWith("file:") ||
      rawThumb.startsWith("content:") ||
      rawThumb.startsWith("data:"));

  const primary = hasValidThumb ? rawThumb : rawDownload;
  const fallback = hasValidThumb && rawDownload && rawDownload !== primary ? rawDownload : undefined;

  const sanitize = (u: string) => (u.includes(" ") ? encodeURI(u) : u);

  return {
    primaryUrl: sanitize(primary),
    fallbackUrl: fallback ? sanitize(fallback) : undefined,
  };
}

/**
 * Detects whether a media item represents an image based on MIME type, file name, or URL.
 * @param {string} [contentType] - The MIME content type.
 * @param {string} [fileName] - The file name.
 * @param {string} [url] - The resource URL.
 * @returns {boolean} True if image.
 */
export function isImageMedia(contentType?: string, fileName?: string, url?: string): boolean {
  const normType = contentType?.toLowerCase()?.trim() || "";
  if (normType.startsWith("image/")) return true;

  const imageExtRegex = /\.(jpe?g|png|gif|webp|bmp|svg|heic|tiff?)(\?.*)?$/i;
  if (fileName && imageExtRegex.test(fileName.trim())) return true;
  if (url && imageExtRegex.test(url.trim())) return true;

  return false;
}

/**
 * Detects whether a media item represents a PDF document based on MIME type, file name, or URL.
 * @param {string} [contentType] - The MIME content type.
 * @param {string} [fileName] - The file name.
 * @param {string} [url] - The resource URL.
 * @returns {boolean} True if PDF.
 */
export function isPdfMedia(contentType?: string, fileName?: string, url?: string): boolean {
  const normType = contentType?.toLowerCase()?.trim() || "";
  if (normType === "application/pdf") return true;

  const pdfExtRegex = /\.pdf(\?.*)?$/i;
  if (fileName && pdfExtRegex.test(fileName.trim())) return true;
  if (url && pdfExtRegex.test(url.trim())) return true;

  return false;
}


/**
 * Detects whether a media item represents a document resource (Office docx/xlsx/pptx, RTF, TXT, ODT, CSV, scan).
 * @param {string} [contentType] - The MIME content type.
 * @param {string} [fileName] - The file name.
 * @param {string} [url] - The resource URL.
 * @returns {boolean} True if document.
 */
export function isDocumentMedia(contentType?: string, fileName?: string, url?: string): boolean {
  const normType = contentType?.toLowerCase()?.trim() || "";
  if (
    normType.startsWith("text/") ||
    normType.includes("word") ||
    normType.includes("document") ||
    normType.includes("officedocument") ||
    normType.includes("sheet") ||
    normType.includes("excel") ||
    normType.includes("presentation") ||
    normType.includes("powerpoint") ||
    normType === "application/rtf" ||
    normType === "application/msword"
  ) {
    return true;
  }

  const docExtRegex = /\.(docx?|txt|rtf|odt|pages|csv|xlsx?|pptx?|scan|md)(\?.*)?$/i;
  if (fileName && docExtRegex.test(fileName.trim())) return true;
  if (url && docExtRegex.test(url.trim())) return true;

  return false;
}

